import { inputTypesEnum } from '../../../../../form-builder/enums/input-type';
import { ReflectionParamTypeEnum } from './enums/reflection-param-type.enum';
import { ReflectionRecipientTypeEnum } from './enums/reflection-recipient-type.enum';
import { isParamVisible } from './reflection-form.factory';
import {
  ConditionSet,
  ReflectionField,
  ReflectionParam,
} from './reflections.models';

const PLACEHOLDER = /\{\{\s*field:([^{}\s]+)\s*\}\}/g;

const asStrings = (value: unknown): string[] =>
  (Array.isArray(value) ? value : [value]).filter(
    (item): item is string => typeof item === 'string' && item !== ''
  );

/** The field keys one value of a param references (the backend `HookConfigFields::walk`). */
function fieldsOfValue(param: ReflectionParam, value: unknown): string[] {
  if (value === null || value === undefined) return [];
  switch (param.type) {
    case ReflectionParamTypeEnum.FIELD_KEY:
    case ReflectionParamTypeEnum.FIELD_KEY_LIST:
      return asStrings(value);
    case ReflectionParamTypeEnum.LOCALIZED_TEXT: {
      if (!param.placeholders) return [];
      const text = value as { en?: unknown; ar?: unknown };
      return [text.en, text.ar]
        .filter((part): part is string => typeof part === 'string')
        .flatMap(part =>
          [...part.matchAll(PLACEHOLDER)].map(match => match[1])
        );
    }
    case ReflectionParamTypeEnum.RECIPIENTS:
      return (Array.isArray(value) ? value : [])
        .filter(row => row?.type === ReflectionRecipientTypeEnum.USER_FIELD)
        .flatMap(row => asStrings(row.field_key));
    case ReflectionParamTypeEnum.MAP: {
      const { key_param, value_param } = param;
      if (!key_param || !value_param) return [];
      return (Array.isArray(value) ? value : []).flatMap(entry => [
        ...(entry && 'key' in entry ? fieldsOfValue(key_param, entry.key) : []),
        ...(entry && 'value' in entry
          ? fieldsOfValue(value_param, entry.value)
          : []),
      ]);
    }
    default:
      return [];
  }
}

/**
 * Every field key a hook config references (the default set a form-scope reflection watches). Mirrors the
 * backend `HookConfigFields::extract` over the visible params: `field_key` / `field_key_list` values,
 * `user_field` recipients, `{{field:x}}` placeholders of localized text that takes placeholders (both languages),
 * and both sides of a `map` (itself walked the same way, so nested maps and texts count).
 */
export function extractConfigFields(
  schema: ReflectionParam[],
  config: Record<string, unknown>
): string[] {
  const keys = schema
    .filter(param => isParamVisible(param, config))
    .flatMap(param => fieldsOfValue(param, config[param.key]));
  return [...new Set(keys)];
}

const asIds = (value: unknown): number[] =>
  (Array.isArray(value) ? value : []).filter(
    (id): id is number => typeof id === 'number'
  );

/** Ids of the users a config stores (`user_list` params and `users` recipients), once each. */
export function extractConfigUserIds(
  schema: ReflectionParam[],
  config: Record<string, unknown>
): number[] {
  const ids = schema.flatMap(param => {
    const value = config[param.key];
    if (param.type === ReflectionParamTypeEnum.USER_LIST) return asIds(value);
    if (param.type !== ReflectionParamTypeEnum.RECIPIENTS) return [];
    return (Array.isArray(value) ? value : [])
      .filter(row => row?.type === ReflectionRecipientTypeEnum.USERS)
      .flatMap(row => asIds(row.user_ids));
  });
  return [...new Set(ids)];
}

/** Ids of the users the rules on user lookup fields compare against (`is`, `is_not`, `is_any_of`), once each. */
export function extractConditionUserIds(
  conditions: ConditionSet | null | undefined,
  fields: ReflectionField[]
): number[] {
  const userFields = new Set(
    fields
      .filter(field => field.type === inputTypesEnum.LOOKUP_USER)
      .map(field => field.value)
  );
  const ids = (conditions?.rules ?? [])
    .filter(rule => userFields.has(rule.field_key))
    .flatMap(rule =>
      asIds(Array.isArray(rule.value) ? rule.value : [rule.value])
    );
  return [...new Set(ids)];
}
